import React, { useState } from 'react';
import { Alert, Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { EntityEditDialog } from '@lp/lp-ui';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';

interface BulkEnableApplicationsDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (clientIds: number[], enable: boolean) => void;
}

const BulkEnableApplicationsDialog: React.FC<BulkEnableApplicationsDialogProps> = ({ open, onClose, onSave }) => {
    const applications = useCache(CachedEntity.Application);
    const [selectedApps, setSelectedApps] = useState<{ [key: number]: boolean }>({});
    const [action, setAction] = useState<string>('');

    const allSelected = applications.length > 0 && applications.every(app => selectedApps[app.id]);
    const someSelected = applications.some(app => selectedApps[app.id]);
    const hasSelection = Object.values(selectedApps).some(Boolean);

    const handleToggle = (appId: number) => {
        setSelectedApps(prev => ({
            ...prev,
            [appId]: !prev[appId]
        }));
    };

    const handleSelectAll = () => {
        const newSelection: { [key: number]: boolean } = {};
        applications.forEach(app => {
            newSelection[app.id] = !allSelected;
        });
        setSelectedApps(newSelection);
    };

    const handleClose = () => {
        setSelectedApps({});
        setAction('');
        onClose();
    };

    const handleSave = () => {
        const clientIds = Object.keys(selectedApps)
            .filter(id => selectedApps[Number(id)])
            .map(Number);
        onSave(clientIds, action === 'enable');
        setSelectedApps({});
        setAction('');
    };

    return (
        <EntityEditDialog
            open={open}
            maxWidth="sm"
            fullWidth
            title="Applications"
            handleClose={handleClose}
            handleSave={handleSave}
            editing={true}
            disableSave={!hasSelection || !action}
        >
            <Alert severity="info" sx={{ mb: 2 }}>
                This action will be executed in the background and it may take some time for all changes to take effect.
            </Alert>
            <Box>
                {applications.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No applications available.
                    </Typography>
                )}
                <FormGroup>
                    {applications.length > 0 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={handleSelectAll}
                                />
                            }
                            label="Select All"
                            sx={{ fontWeight: 'bold', borderBottom: 1, borderColor: 'divider', pb: 1, mb: 1 }}
                        />
                    )}
                    {applications.map(app => (
                        <FormControlLabel
                            key={app.id}
                            control={
                                <Checkbox
                                    checked={selectedApps[app.id] || false}
                                    onChange={() => handleToggle(app.id)}
                                />
                            }
                            label={app.label ? app.label : app.key}
                        />
                    ))}
                </FormGroup>
            </Box>
            <Box sx={{ mt: 2 }}>
                <FormControl>
                    <FormLabel>Action</FormLabel>
                    <RadioGroup row value={action} onChange={(e) => setAction(e.target.value)}>
                        <FormControlLabel value="enable" control={<Radio />} label="Enable" />
                        <FormControlLabel value="disable" control={<Radio />} label="Disable" />
                    </RadioGroup>
                </FormControl>
            </Box>
        </EntityEditDialog>
    );
};

export default BulkEnableApplicationsDialog;
